/**
 * Cognito managed login with the authorization code flow + PKCE. No library: three small calls.
 * The access token goes to the API; API Gateway verifies it before any /admin route runs.
 */
const DOMAIN = import.meta.env['VITE_COGNITO_DOMAIN'] as string;
const CLIENT_ID = import.meta.env['VITE_COGNITO_CLIENT_ID'] as string;
const REDIRECT = `${location.origin}/callback`;

interface Tokens {
  access: string;
  id: string;
  expiresAt: number;
}

const SESSION_KEY = 'nsd-admin-tokens';
const REFRESH_KEY = 'nsd-admin-refresh';
const PKCE_KEY = 'nsd-admin-pkce';

const b64url = (bytes: ArrayBuffer | Uint8Array) =>
  btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

function readTokens(): Tokens | null {
  try {
    return JSON.parse(sessionStorage.getItem(SESSION_KEY) ?? 'null') as Tokens | null;
  } catch {
    return null;
  }
}

function saveTokens(r: { access_token: string; id_token: string; expires_in: number; refresh_token?: string }): void {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify({ access: r.access_token, id: r.id_token, expiresAt: Date.now() + r.expires_in * 1000 }));
  if (r.refresh_token) localStorage.setItem(REFRESH_KEY, r.refresh_token);
}

async function tokenRequest(body: Record<string, string>) {
  const res = await fetch(`https://${DOMAIN}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ client_id: CLIENT_ID, ...body }),
  });
  if (!res.ok) throw new Error(`Sign-in failed (${res.status})`);
  return (await res.json()) as { access_token: string; id_token: string; expires_in: number; refresh_token?: string };
}

export async function login(): Promise<never> {
  const verifier = b64url(crypto.getRandomValues(new Uint8Array(48)));
  const state = b64url(crypto.getRandomValues(new Uint8Array(16)));
  const challenge = b64url(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)));
  sessionStorage.setItem(PKCE_KEY, JSON.stringify({ verifier, state, back: location.pathname + location.search }));
  const q = new URLSearchParams({
    response_type: 'code',
    client_id: CLIENT_ID,
    redirect_uri: REDIRECT,
    scope: 'openid profile',
    code_challenge_method: 'S256',
    code_challenge: challenge,
    state,
  });
  location.assign(`https://${DOMAIN}/oauth2/authorize?${q}`);
  return new Promise<never>(() => {});
}

/** Finishes the redirect from Cognito; returns the path the user was on before signing in. */
export async function handleCallback(): Promise<string> {
  const q = new URLSearchParams(location.search);
  const saved = JSON.parse(sessionStorage.getItem(PKCE_KEY) ?? 'null') as { verifier: string; state: string; back: string } | null;
  sessionStorage.removeItem(PKCE_KEY);
  if (!saved || q.get('state') !== saved.state || !q.get('code')) throw new Error('Sign-in was interrupted. Please try again.');
  saveTokens(await tokenRequest({ grant_type: 'authorization_code', code: q.get('code')!, redirect_uri: REDIRECT, code_verifier: saved.verifier }));
  return saved.back && saved.back !== '/callback' ? saved.back : '/';
}

/** A valid access token, refreshed when it is about to expire; sends the user to sign in otherwise. */
export async function accessToken(): Promise<string> {
  const t = readTokens();
  if (t && t.expiresAt - 60_000 > Date.now()) return t.access;
  const refresh = localStorage.getItem(REFRESH_KEY);
  if (refresh) {
    try {
      saveTokens(await tokenRequest({ grant_type: 'refresh_token', refresh_token: refresh }));
      return readTokens()!.access;
    } catch {
      localStorage.removeItem(REFRESH_KEY);
    }
  }
  return login();
}

export function signedInUser(): string | null {
  const t = readTokens();
  if (!t) return null;
  try {
    const payload = JSON.parse(atob(t.id.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>;
    return String(payload['cognito:username'] ?? payload['preferred_username'] ?? '');
  } catch {
    return null;
  }
}

export function logout(): void {
  sessionStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(REFRESH_KEY);
  location.assign(`https://${DOMAIN}/logout?${new URLSearchParams({ client_id: CLIENT_ID, logout_uri: `${location.origin}/` })}`);
}
